import { useState } from 'react';
import { useValidateEvent } from '@ion/api';
import { isValidJSONObject } from '@ion/mrdash';

const UNABLE_TO_VALIDATE_MESSAGE = 'Unable to validate event';

// Owns the input event validation chain. The combined `hasValidInputEvent`
// requires every validator to pass; tabs gate on it.
//
// Validators today:
//   - isValidJson:  sync, the editor's JSON-parse check
//   - isValidEvent: async, the /v1.1/test/validate_event API call
//
// `isValidating` is set explicitly while the API call is in flight so callers
// don't have to infer pending state from "not yet true".
export default function useEventValidation({ clusterId }) {
  const [isValidJson, setIsValidJson] = useState(false);
  const [isValidEvent, setIsValidEvent] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const hasValidInputEvent = isValidJson && isValidEvent;

  const onCompleted = buildOnValidateCompleted({ setIsValidating, setIsValidEvent, setValidationError });
  const onError = buildOnValidateError({ setIsValidating, setIsValidEvent, setValidationError });

  const [executeValidateEvent] = useValidateEvent({ onCompleted, onError });

  const validate = buildValidate({
    clusterId,
    executeValidateEvent,
    setIsValidJson,
    setIsValidEvent,
    setIsValidating,
    setValidationError,
  });

  return {
    setIsValidJson,
    hasValidInputEvent,
    validatingEvent: isValidating,
    validationError,
    validate,
  };
}

function buildOnValidateCompleted({ setIsValidating, setIsValidEvent, setValidationError }) {
  return data => {
    setIsValidating(false);
    if (!data) {
      setValidationError(UNABLE_TO_VALIDATE_MESSAGE);
      setIsValidEvent(false);
    } else if (data.error) {
      setValidationError(data.error);
      setIsValidEvent(false);
    } else {
      setValidationError(null);
      setIsValidEvent(true);
    }
  };
}

function buildOnValidateError({ setIsValidating, setIsValidEvent, setValidationError }) {
  return () => {
    setIsValidating(false);
    setValidationError(UNABLE_TO_VALIDATE_MESSAGE);
    setIsValidEvent(false);
  };
}

function buildValidate({
  clusterId,
  executeValidateEvent,
  setIsValidJson,
  setIsValidEvent,
  setIsValidating,
  setValidationError,
}) {
  return eventString => {
    setValidationError(null);
    setIsValidEvent(false);

    if (!isValidJSONObject(eventString)) {
      setIsValidJson(false);
      setIsValidating(false);
      return;
    }

    setIsValidJson(true);
    setIsValidating(true);
    executeValidateEvent({
      clusterId,
      inputEvent: JSON.parse(eventString),
    });
  };
}
